/* 
 * Copyright (C) 2022 SHC Holdings LLC.  All Rights Reserved. 
 */
import React, { useMemo } from 'react';
import {
  useFilterEditingValue,
  useLookupConditionSortByType,
  useConditionCount,
  ConditionType,
  StyledPrimaryConditionSection,
  SimpleCheckbox,
  OptionType,
  TransformedCondition,
} from 'nmqf-common-ui';
import { FormGroup } from 'reactstrap';

type LeafRelation = string | [string, string[]];
type RelationCollection = Array<LeafRelation | [string, RelationCollection]>;

const RAW_RELATION_CONDITIONS: RelationCollection = [
  ['83', ['84', '85', '86', '87', '88', '89', '90']],
  ['91', [['92', ['93', '94', '95', '96', '97', '98']], ['99', ['100', '101', '102']], '103', ['104', ['105', '106', '107', '108', '109']]]],
  ['110', ['111', '112', '113']],
  ['114', ['115', '116', '117']],
  '118',
  '119',
  ['120', ['121', '122', '123']],
  ['124', ['125', '126', '127', '128', '129', '130', '131', '132', '133', '134']],
  ['135', ['136', '137']],
  '158',
  '159',
  ['160', ['161', '162', '163', '164', '165']],
];

const buildRelationship = (options: RelationCollection, acc: Record<string, { parent?: string; children?: string[] }> = Object.assign({}), parent?: string) => {
  options.forEach((option) => {
    const value = Array.isArray(option) ? option[0] : option;
    const children = Array.isArray(option) ? option[1] : undefined;
    acc[value] = { parent, children: children?.map((child: any) => (Array.isArray(child) ? child[0] : child)) };
    children && buildRelationship(children, acc, value);
  });
  return acc;
};

const CONDITIONS_RELATIONSHIP_MAP = buildRelationship(RAW_RELATION_CONDITIONS);

const processCheckedValue = (keyConditionMap: Record<string, OptionType>, values: string[], value: string) => {
  let result = [...values, value];

  const removeAllChildren = (code: string) => {
    const { children } = CONDITIONS_RELATIONSHIP_MAP[code];
    if (children) {
      children.forEach((child) => {
        result = result.filter((v) => v !== child);
        removeAllChildren(child);
      });
    }
  };
  removeAllChildren(value);

  const checkParentRelation = (code: string) => {
    const { parent } = CONDITIONS_RELATIONSHIP_MAP[code];
    if (parent) {
      const { children } = CONDITIONS_RELATIONSHIP_MAP[parent];
      const isSelectedAll = children?.every((child) => !keyConditionMap[child] || result.some((v) => v === child));
      if (isSelectedAll) {
        result = [parent, ...result.filter((v) => !children?.some((child) => child === v))];
        checkParentRelation(parent);
      }
    }
  };
  checkParentRelation(value);

  return result;
};

const processUncheckValue = (keyConditionMap: Record<string, OptionType>, values: string[], value: string) => {
  let result = values.filter((v) => v !== value);
  const { parent } = CONDITIONS_RELATIONSHIP_MAP[value];

  if (parent && result.length === values.length) {
    const { children = [] } = CONDITIONS_RELATIONSHIP_MAP[parent];
    result = [...result, ...children.filter((child) => keyConditionMap[child] && child !== value)];
    result = processUncheckValue(keyConditionMap, result, parent);
  }

  return result;
};

const detectIndeterminate = (keyConditionMap: Record<string, OptionType>, values: string[], value: string): boolean => {
  let indeterminate = false;
  const { children = [] } = CONDITIONS_RELATIONSHIP_MAP[value];
  for (const child of children) {
    indeterminate = !!keyConditionMap[child] && (values.some((v) => v === child) || detectIndeterminate(keyConditionMap, values, child));
    if (indeterminate) {
      return indeterminate;
    }
  }
  return indeterminate;
};

const searchCondition = (keyConditionMap: Record<string, OptionType>, value: string, textSearch?: string) => {
  const isIncludeSearchText = (code: string) => !textSearch || keyConditionMap?.[code]?.label.toLowerCase().includes(textSearch.toLowerCase());
  let isShow = isIncludeSearchText(value);

  const checkChildren = (code: string): boolean => {
    return !!CONDITIONS_RELATIONSHIP_MAP[code].children?.some((child) => isIncludeSearchText(child) || checkChildren(child));
  };
  isShow = isShow || checkChildren(value);

  const checkParent = (code: string): boolean => {
    const { parent } = CONDITIONS_RELATIONSHIP_MAP[code];
    return !!parent && (isIncludeSearchText(parent) || checkParent(parent));
  };
  isShow = isShow || checkParent(value);

  return isShow;
};

export const PrioritizedConditionSection = () => {
  const [textSearch] = useFilterEditingValue('textSearch');
  const [lookupData] = useLookupConditionSortByType();
  const [countData] = useConditionCount();
  const [selectingConditions, setSelectingConditions] = useFilterEditingValue('comorbidities');

  const keyConditionMap: Record<string, TransformedCondition> = useMemo(
    () =>
      lookupData.groupedCondition?.[ConditionType.Key]?.reduce((acc, curr) => {
        acc[curr.value] = curr;
        return acc;
      }, Object.assign({})) || Object.assign({}),
    [lookupData.groupedCondition]
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    let values = selectingConditions ? [...selectingConditions] : [];
    if (event.target.checked) {
      values = processCheckedValue(keyConditionMap, values, value);
    } else {
      values = processUncheckValue(keyConditionMap, values, value);
    }
    setSelectingConditions(values.sort(lookupData.sortValue));
  };
  const renderConditionTree = (options: RelationCollection, isParentChecked?: boolean) => {
    return (
      <>
        {options.map((option) => {
          const value = Array.isArray(option) ? option[0] : option;
          const children = Array.isArray(option) ? option[1] : undefined;
          const checked = isParentChecked || selectingConditions?.some((v) => v === value);
          const condition = keyConditionMap[value];
          const indeterminate = !checked && detectIndeterminate(keyConditionMap, selectingConditions || [], value);
          const isShow = searchCondition(keyConditionMap, value, textSearch);
          return (
            condition &&
            isShow && (
              <React.Fragment key={value}>
                <FormGroup check>
                  <SimpleCheckbox
                    indeterminate={indeterminate}
                    value={value}
                    checked={checked}
                    onChange={handleChange}
                    label={countData.getLabelWithCount(condition)}
                  />
                </FormGroup>
                <div className="ml-4">{children && renderConditionTree(children, checked)}</div>
              </React.Fragment>
            )
          );
        })}
      </>
    );
  };

  return <StyledPrimaryConditionSection tag="fieldset">{renderConditionTree(RAW_RELATION_CONDITIONS)}</StyledPrimaryConditionSection>;
};
